import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Link
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import { Release } from '../types';

interface ReleaseDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  release: Release | null;
  onEdit: () => void;
  onDelete: () => void;
}

const ReleaseDetailsDialog: React.FC<ReleaseDetailsDialogProps> = ({
  open,
  onClose,
  release,
  onEdit,
  onDelete
}) => {
  if (!release) return null;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Released':
        return 'success';
      case 'In Progress':
        return 'info';
      case 'Planned':
        return 'default';
      default:
        return 'default';
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          margin: { xs: 2, sm: 3 },
          maxHeight: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 64px)' },
          width: { xs: 'calc(100% - 32px)', sm: '100%' }
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6" component="span">
            {release.version}
          </Typography>
          <Chip label={release.status} color={getStatusColor(release.status)} size="small" />
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {release.description && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Description
              </Typography>
              <Box sx={{ 
                '& h1': { fontSize: '1.5rem', fontWeight: 'bold', mt: 2, mb: 1 },
                '& h2': { fontSize: '1.3rem', fontWeight: 'bold', mt: 2, mb: 1 },
                '& h3': { fontSize: '1.1rem', fontWeight: 'bold', mt: 1.5, mb: 0.5 },
                '& p': { mb: 1 },
                '& ul, & ol': { pl: 2, mb: 1 },
                '& li': { mb: 0.5 },
                '& code': { 
                  backgroundColor: '#f5f5f5', 
                  padding: '2px 6px', 
                  borderRadius: '3px',
                  fontFamily: 'monospace',
                  fontSize: '0.9em'
                },
                '& pre': { 
                  backgroundColor: '#f5f5f5', 
                  padding: '12px', 
                  borderRadius: '4px',
                  overflow: 'auto',
                  mb: 1
                },
                '& a': { color: 'primary.main', textDecoration: 'underline' }
              }}>
                <ReactMarkdown>{release.description}</ReactMarkdown>
              </Box>
            </Box>
          )}

          {release.releaseDate && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                {release.status === 'Planned' ? 'Expected Release Date' : 'Release Date'}
              </Typography>
              <Typography variant="body1">
                {new Date(release.releaseDate).toLocaleDateString()}
              </Typography>
            </Box>
          )}

          {release.pullRequestUrl && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                GitHub Release
              </Typography>
              <Link 
                href={release.pullRequestUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
              >
                View on GitHub
              </Link>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        {/* Hidden: Delete and Edit buttons
        <Button onClick={onDelete} color="error">
          Delete
        </Button>
        <Button onClick={onEdit}>Edit</Button>
        */}
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReleaseDetailsDialog;
